import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CandidateOptionCard } from './CandidateOptionCard'
import { RecruitmentSuccess } from './RecruitmentSuccess'
import { RECRUITMENT_SOURCE_INFO } from '@/constants/recruitmentSources'
import { RECRUITMENT_SOURCES } from '@/constants/recruitment'
import { generateCandidateOptions, type GeneratedCandidate } from '@/lib/candidateGenerator'
import { executeRecruitmentInstantly } from '@/services/recruitment'
import { candidateKeys } from '@/services/candidates'
import { actionPointKeys } from '@/services/actionPoints'
import { partyActionKeys } from '@/services/partyActions'
import type { IdeologyVector, RecruitmentSource, Candidate } from '@/types'
import { Loader2 } from 'lucide-react'

type ModalStep = 'select_candidate' | 'success'

interface CandidateScoutingModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  source: RecruitmentSource
  partyId: string
  nationId: string
  partyIdeology: IdeologyVector
  currentTick: number
  onRecruited?: (candidate: Candidate) => void
  onAssignRole?: (candidate: Candidate) => void
}

function createCandidates(
  partyId: string,
  partyIdeology: IdeologyVector,
  source: RecruitmentSource,
  currentTick: number
): GeneratedCandidate[] {
  return generateCandidateOptions(
    {
      partyId,
      partyIdeology,
      recruitmentSource: source,
      currentTick,
    },
    3
  )
}

export function CandidateScoutingModal({
  open,
  onOpenChange,
  source,
  partyId,
  nationId,
  partyIdeology,
  currentTick,
  onRecruited,
  onAssignRole,
}: CandidateScoutingModalProps) {
  const queryClient = useQueryClient()
  const [candidates, setCandidates] = useState<GeneratedCandidate[]>(() =>
    createCandidates(partyId, partyIdeology, source, currentTick)
  )
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [step, setStep] = useState<ModalStep>('select_candidate')
  const [recruitedCandidate, setRecruitedCandidate] = useState<Candidate | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sourceInfo = RECRUITMENT_SOURCE_INFO[source]
  const sourceConfig = RECRUITMENT_SOURCES[source]

  const handleScoutAgain = () => {
    const options = createCandidates(partyId, partyIdeology, source, currentTick)
    setCandidates(options)
    setSelectedIndex(null)
    setError(null)
  }

  const handleRecruit = async () => {
    if (selectedIndex === null) return

    const candidate = candidates[selectedIndex]
    setIsSubmitting(true)
    setError(null)

    try {
      const result = await executeRecruitmentInstantly({
        partyId,
        nationId,
        tick: currentTick,
        source,
        candidateData: {
          name: candidate.name,
          age: candidate.age,
          ideology_vector: candidate.ideology_vector,
          charisma: candidate.charisma,
          admin_skill: candidate.admin_skill,
          debate_skill: candidate.debate_skill,
          scandal_prone: candidate.scandal_prone,
          ambition: candidate.ambition,
          loyalty: candidate.loyalty,
          recognition: candidate.recognition,
        },
      })

      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: candidateKeys.activeByParty(partyId) })
      queryClient.invalidateQueries({ queryKey: candidateKeys.byParty(partyId) })
      queryClient.invalidateQueries({
        queryKey: actionPointKeys.byPartyTick(partyId, currentTick),
      })
      queryClient.invalidateQueries({ queryKey: partyActionKeys.byParty(partyId) })

      if (result.success && result.candidate) {
        setRecruitedCandidate(result.candidate)
        setStep('success')
        onRecruited?.(result.candidate)
      } else {
        setError(result.failureReason ?? 'Recruitment failed.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRecruitAnother = () => {
    setStep('select_candidate')
    setRecruitedCandidate(null)
    setSelectedIndex(null)
    setError(null)
    const options = createCandidates(partyId, partyIdeology, source, currentTick)
    setCandidates(options)
  }

  const handleAssignRole = () => {
    if (recruitedCandidate) {
      onAssignRole?.(recruitedCandidate)
    }
    onOpenChange(false)
  }

  const handleDone = () => {
    onOpenChange(false)
  }

  const selectedCandidate = selectedIndex !== null ? candidates[selectedIndex] : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        {step === 'select_candidate' && (
          <>
            <DialogHeader>
              <DialogTitle>Scouting: {sourceInfo.name}</DialogTitle>
              <DialogDescription>
                Select a candidate to recruit to your party. They will join immediately.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3">
              {candidates.map((candidate, index) => (
                <CandidateOptionCard
                  key={`${candidate.name}-${index}`}
                  candidate={candidate}
                  partyIdeology={partyIdeology}
                  selected={selectedIndex === index}
                  onSelect={() => setSelectedIndex(selectedIndex === index ? null : index)}
                />
              ))}
            </div>

            {error && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={handleScoutAgain} disabled={isSubmitting}>
                Scout Again
              </Button>
              <Button onClick={handleRecruit} disabled={selectedIndex === null || isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting
                  ? 'Recruiting...'
                  : selectedCandidate
                    ? `Recruit (${sourceConfig.apCost} AP)`
                    : 'Select a Candidate'}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'success' && recruitedCandidate && (
          <RecruitmentSuccess
            candidate={recruitedCandidate}
            partyIdeology={partyIdeology}
            onAssignRole={handleAssignRole}
            onRecruitAnother={handleRecruitAnother}
            onDone={handleDone}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
